/**
 * ของสวมใส่ — กฎว่าใส่อะไรช่องไหนได้ และของที่ใส่อยู่ให้ค่าเท่าไร
 *
 * **อยู่ใน shared เพราะทั้งสองฝั่งต้องได้ตัวเลขเดียวกัน**
 * server เป็นผู้ตัดสินว่าใส่ได้จริงไหม ส่วน client เอาไปแสดงให้เห็นก่อนกด
 * ถ้าคิดคนละสูตร ตัวเลขในหน้าต่างตัวละครจะไม่ตรงกับดาเมจที่ออกจริง
 *
 * ทุกฟังก์ชันในไฟล์นี้เป็น pure function ไม่สุ่มและไม่อ่าน state
 */
import { EQUIP_SLOTS, STAT_KEYS } from "./types.js";
import { CLASSES } from "./data.js";
import { refineBonus } from "./refine.js";
/**
 * อ่านค่าจาก "อะไรก็ตามที่หน้าตาเหมือน map"
 *
 * **ห้ามใช้ `instanceof Map`** `MapSchema` ของ Colyseus มี `.get()` ให้ใช้
 * แต่ไม่ได้สืบทอดจาก `Map` จริง เช็คด้วย instanceof แล้วจะตกไปอ่านแบบ object
 * ซึ่งได้ undefined ทุกครั้งโดยไม่มี error — เคยทำให้ "สวมของไม่ได้ทั้งที่มีของ"
 */
export function readMap(src, key) {
    const anySrc = src;
    if (typeof anySrc.get === "function")
        return anySrc.get(key);
    return src[key];
}
/** คู่ key/value จากทั้ง Map, MapSchema และ object ธรรมดา */
export function mapEntries(src) {
    const anySrc = src;
    if (typeof anySrc.entries === "function")
        return [...anySrc.entries()];
    return Object.entries(src);
}
/** ช่องที่ของชิ้นนี้ลงได้ (ต่างหูลงได้สองช่อง) */
export function slotsFor(kind) {
    return EQUIP_SLOTS.filter((s) => s.kind === kind).map((s) => s.id);
}
/** ช่องนี้รับของชนิดไหน คืน undefined ถ้าไม่ใช่ช่องที่มีจริง */
export function kindOfSlot(slot) {
    return EQUIP_SLOTS.find((s) => s.id === slot)?.kind;
}
/** อาวุธชิ้นนี้เป็นอาวุธสองมือหรือไม่ */
export function isTwoHanded(def) {
    return def?.kind === "equip" && def.slot === "weapon" && def.hands === 2;
}
/**
 * ใส่ของชิ้นนี้ลงช่องนี้ได้ไหม คืน undefined ถ้าได้
 *
 * แยกเหตุผลออกมาเป็นรหัส ไม่ใช่ข้อความไทย เพราะ client ต้องเลือกคำเอง
 * และ **เทสต์ต้องไม่ผูกกับข้อความ** ไม่งั้นแก้คำเมื่อไรเทสต์ล้มทันที
 */
export function equipDenial(def, slot, classId, baseLevel, weaponDef) {
    if (!def || def.kind !== "equip" || !def.slot)
        return "not-equip";
    if (kindOfSlot(slot) !== def.slot)
        return "wrong-slot";
    if (baseLevel < (def.reqLevel ?? 1))
        return "level";
    const cls = def.reqClass;
    if (cls && cls.length > 0 && !cls.includes(classId))
        return "class";
    // ถ้าใส่อาวุธสองมืออยู่ แล้วพยายามใส่ของช่อง offhand (มือรอง)
    if (slot === "offhand" && isTwoHanded(weaponDef))
        return "two-handed";
    // ท่าถือของอาชีพ (ClassDef.weapons) ของที่ไม่มีชนิดไม่ถูกจำกัด
    if ((def.slot === "weapon" || def.slot === "offhand") && def.weaponType) {
        const allowed = CLASSES[classId]?.weapons;
        if (allowed && !allowed.includes(def.weaponType))
            return "wrong-weapon";
    }
    return undefined;
}
/** ชนิดอาวุธมือหลัก (ไม่ใช่ของมือรอง) — ใช้เลือกท่าถือตอนมือเปล่า */
const OFFHAND_TYPES = new Set([
    "shield", "buckler", "tome", "orb", "quiver", "dagger_off",
]);
/** ชนิดนี้เป็นของมือรองหรือไม่ */
export function isOffhandType(t) {
    return OFFHAND_TYPES.has(t);
}
/**
 * ท่าถือของผู้เล่นจากของที่สวมอยู่
 *
 * **มือเปล่า = อาวุธประจำอาชีพ** (ชนิดมือหลักตัวแรกใน `ClassDef.weapons`)
 * เพราะตัวละครถืออาวุธประจำอาชีพอยู่แล้วตอนไม่ได้สวมอะไร ค่าพลังก็คิดจาก `ClassDef.weaponAtk`
 */
export function heldWeapons(classId, equipped, items) {
    const w = items[readMap(equipped, "weapon") ?? ""]?.weaponType;
    const o = items[readMap(equipped, "offhand") ?? ""]?.weaponType;
    return {
        weapon: w ?? CLASSES[classId]?.weapons?.find((t) => !isOffhandType(t)),
        offhand: o,
    };
}
/**
 * ช่องที่ควรใส่ให้อัตโนมัติเมื่อผู้เล่นกดของในกระเป๋าโดยไม่ได้เลือกช่อง
 *
 * ต่างหูมีสองช่อง **ถ้ามีช่องว่างให้ลงช่องว่างก่อน** ไม่ใช่ทับของที่ใส่อยู่
 * ไม่งั้นใส่ต่างหูวงที่สองแล้ววงแรกเด้งกลับกระเป๋าโดยไม่ได้ตั้งใจ
 */
export function autoSlot(def, equipped) {
    if (!def || def.kind !== "equip" || !def.slot)
        return undefined;
    const get = (id) => readMap(equipped, id) ?? "";
    const slots = slotsFor(def.slot);
    return slots.find((id) => !get(id)) ?? slots[0];
}
/**
 * รวมค่าจากของทุกชิ้นที่สวมอยู่
 *
 * **อาวุธแทนค่าของอาชีพ ไม่ใช่บวกทับ** (`weaponAtk` ใน `DerivedModifiers`
 * ถูกออกแบบมาให้แทนที่อยู่แล้ว) ส่วนเกราะและเครื่องประดับเป็นบวกสะสม
 * ของที่หาไม่เจอใน items.json ถูกข้ามไปเงียบ ๆ แทนที่จะโยน error
 * เพราะอาจเป็นของเก่าที่ถูกถอดออกจากเกมแล้ว แต่ยังค้างในฐานข้อมูลของผู้เล่น
 *
 * หากระบุ `refines` (แผนที่ระดับตีบวกต่อช่อง 0–10) จะคำนวณโบนัสตีบวกให้ด้วย
 */
export function equipTotals(equipped, items, refines) {
    const mods = {};
    const stats = {};
    let flatDef = 0;
    let flatCrit = 0;
    let flatBlock = 0;
    let aspdMul = 1;
    let hpMul = 1;
    const entries = mapEntries(equipped);
    for (const [slot, itemId] of entries) {
        if (!itemId || !kindOfSlot(slot))
            continue;
        const def = items[itemId];
        const b = def?.bonus;
        if (!b)
            continue;
        const refineLvl = refines ? (readMap(refines, slot) ?? 0) : 0;
        if (b.weaponAtk !== undefined) {
            mods.weaponAtk = b.weaponAtk + refineBonus(b.weaponAtk, refineLvl);
        }
        if (b.weaponMatk !== undefined) {
            mods.weaponMatk = b.weaponMatk + refineBonus(b.weaponMatk, refineLvl);
        }
        flatDef += (b.flatDef ?? 0) + refineBonus(b.flatDef ?? 0, refineLvl);
        const isStatRefined = slot === "wings"
            || slot === "face"
            || slot === "ear1"
            || slot === "ear2"
            || slot === "special"
            || slot === "pet"
            || (!b.weaponAtk && !b.weaponMatk && !b.flatDef);
        flatCrit += (b.flatCrit ?? 0) + (isStatRefined ? refineBonus(b.flatCrit ?? 0, refineLvl) : 0);
        flatBlock += (b.flatBlock ?? 0) + (slot === "offhand" || isStatRefined ? refineBonus(b.flatBlock ?? 0, refineLvl) : 0);
        aspdMul *= b.aspdMul ?? 1;
        hpMul *= b.hpMul ?? 1;
        for (const k of STAT_KEYS) {
            const add = b.stats?.[k];
            if (add) {
                const rf = isStatRefined ? refineBonus(add, refineLvl) : 0;
                stats[k] = (stats[k] ?? 0) + add + rf;
            }
        }
        // สัตว์เลี้ยงเลเวลเต็ม 10: เปลี่ยนร่าง (Evolution) มอบโบนัสพิเศษ All Stats +5, Max HP +5%, DEF +15, CRIT +5%
        if (slot === "pet" && refineLvl >= 10) {
            flatDef += 15;
            flatCrit += 5;
            hpMul *= 1.05;
            for (const k of STAT_KEYS) {
                stats[k] = (stats[k] ?? 0) + 5;
            }
        }
    }
    if (flatDef)
        mods.flatDef = flatDef;
    if (flatCrit)
        mods.flatCrit = flatCrit;
    // ของสวมใส่เขียนบล็อกเป็น % (items.json "flatBlock": 8 = บล็อก +8%) แต่ค่ารองใช้สัดส่วน 0–1
    // เคยบวกตรง ๆ จนใส่โล่ชิ้นไหนก็บล็อกได้ 100% (เจอจากโมเดลสมดุล 9 ต.ค. 2026 — มีเทสต์คุม)
    if (flatBlock)
        mods.flatBlock = flatBlock / 100;
    if (aspdMul !== 1)
        mods.aspdMul = aspdMul;
    if (hpMul !== 1)
        mods.hpMul = hpMul;
    return { mods, stats };
}
/** บวกสเตตัสจากของสวมใส่เข้ากับสเตตัสฐาน คืนชุดใหม่ ไม่แก้ของเดิม */
export function withEquipStats(base, add) {
    const out = { ...base };
    for (const k of STAT_KEYS)
        out[k] += add[k] ?? 0;
    return out;
}
/**
 * รวมตัวปรับจากบัฟกับจากของสวมใส่
 *
 * **บวกกันแบบเดียวกับที่ต่างฝ่ายต่างสะสมมา** ค่าที่เป็นผลคูณ (aspd, hp) คูณกัน
 * ส่วนอาวุธ บัฟไม่มีสิทธิ์เปลี่ยน จึงเอาของสวมใส่เป็นหลักเสมอ
 */
export function mergeModifiers(a, b) {
    const out = {};
    const flat = (x, y) => (x ?? 0) + (y ?? 0);
    const mul = (x, y) => (x ?? 1) * (y ?? 1);
    if (a.weaponAtk !== undefined || b.weaponAtk !== undefined) {
        out.weaponAtk = b.weaponAtk ?? a.weaponAtk;
    }
    if (a.weaponMatk !== undefined || b.weaponMatk !== undefined) {
        out.weaponMatk = b.weaponMatk ?? a.weaponMatk;
    }
    if (a.flatDef || b.flatDef)
        out.flatDef = flat(a.flatDef, b.flatDef);
    if (a.flatCrit || b.flatCrit)
        out.flatCrit = flat(a.flatCrit, b.flatCrit);
    if (a.flatBlock || b.flatBlock)
        out.flatBlock = flat(a.flatBlock, b.flatBlock);
    if (a.aspdMul !== undefined || b.aspdMul !== undefined) {
        out.aspdMul = mul(a.aspdMul, b.aspdMul);
    }
    if (a.hpMul !== undefined || b.hpMul !== undefined) {
        out.hpMul = mul(a.hpMul, b.hpMul);
    }
    if (a.mpMul !== undefined || b.mpMul !== undefined)
        out.mpMul = mul(a.mpMul, b.mpMul);
    if (a.atkMul !== undefined || b.atkMul !== undefined)
        out.atkMul = mul(a.atkMul, b.atkMul);
    if (a.matkMul !== undefined || b.matkMul !== undefined)
        out.matkMul = mul(a.matkMul, b.matkMul);
    if (a.flatHit || b.flatHit)
        out.flatHit = flat(a.flatHit, b.flatHit);
    if (a.flatFlee || b.flatFlee)
        out.flatFlee = flat(a.flatFlee, b.flatFlee);
    return out;
}
//# sourceMappingURL=equip.js.map